import { useSearchParams } from "react-router-dom";
import Sidebar from "../sidebar/Sidebar";
import Footer from "../components/Footer";
import ProjectCard from "../components/ProjectCard";
import ProjectModal from "../components/ProjectModal";
import PageContainer from "../components/PageContainer";
import MentalHealthCard from "../components/MentalHealthCard";
import { BioParagraph } from "../components/HomeBio";
import { schoolProjects as siteSchoolProjects } from "../data/schoolprojects";
import { otherProjects as siteOtherProjects } from "../data/otherprojects";
import { researchProjects as siteResearchProjects } from "../data/researchprojects";
import { bioExpandedParagraphs } from "../data/homeContent";
import { work } from "../data/work";
import { schools } from "../data/schools";
import { fifthrows } from "../data/fifthrows";
import EditableRegion from "../studio/EditableRegion";
import { useStudio } from "../studio/StudioContext";
import {
  mergeProjectsWithDraft,
  projectCardBlurb,
} from "../utils/prePhdProjects";

const TAB_PROFESSIONAL = "professional";
const TAB_EDUCATION = "education";
const TAB_CCAS = "ccas";
const TAB_PROJECTS = "projects";

const TABS = [
  { id: TAB_PROFESSIONAL, label: "Professional" },
  { id: TAB_EDUCATION, label: "Education" },
  { id: TAB_CCAS, label: "Fifth Rows / CCAs" },
  { id: TAB_PROJECTS, label: "Projects" },
];

const PROJECT_KINDS = [
  {
    id: "school",
    draftKey: "schoolProjects",
    regionPrefix: "prePhd.school",
    label: "School",
    title: "Selected Polytechnic / Undergraduate School Projects (2018-2024)",
    blurb:
      "Coursework and capstone projects from polytechnic and undergraduate studies at NP and SUTD.",
    site: siteSchoolProjects,
  },
  {
    id: "research",
    draftKey: "researchProjects",
    regionPrefix: "prePhd.research",
    label: "Research",
    title: "Undergraduate Research Projects (2020-2024)",
    blurb: "Undergraduate Research Opportunities Programme (UROP) projects from my undergraduate years at SUTD.",
    site: siteResearchProjects,
  },
  {
    id: "other",
    draftKey: "otherProjects",
    regionPrefix: "prePhd.other",
    label: "Other",
    title: "Other Projects",
    blurb: "Personal builds and side projects outside formal coursework.",
    site: siteOtherProjects,
  },
];

const PROJECT_GRID_CLASSES =
  "grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-5 lg:gap-8";

const monthMap = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const parseDateToken = (token, isEnd = false) => {
  const trimmed = token.trim();
  if (/present/i.test(trimmed)) return new Date(9999, 11, 31).getTime();
  const parts = trimmed.split(" ");
  if (parts.length === 1) {
    const year = Number(parts[0]);
    return Number.isNaN(year)
      ? 0
      : new Date(year, isEnd ? 11 : 0, isEnd ? 31 : 1).getTime();
  }
  const month = monthMap[parts[0].slice(0, 3).toLowerCase()];
  const year = Number(parts[1]);
  if (month === undefined || Number.isNaN(year)) return 0;
  return new Date(year, month, isEnd ? 31 : 1).getTime();
};

const getDurationParts = (duration) => {
  const [start = "", end = "Present"] = duration.split(" - ");
  return { start: start.trim(), end: end.trim() };
};

const groupByOrganisation = (items) =>
  Object.values(
    items.reduce((acc, item) => {
      if (!acc[item.organisation]) {
        acc[item.organisation] = {
          organisation: item.organisation,
          roles: [],
        };
      }
      acc[item.organisation].roles.push(item);
      return acc;
    }, {}),
  )
    .map((group) => {
      const sortedRoles = [...group.roles].sort((a, b) => {
        const aStart = parseDateToken(getDurationParts(a.duration).start);
        const bStart = parseDateToken(getDurationParts(b.duration).start);
        return bStart - aStart;
      });
      const allStarts = sortedRoles.map((role) =>
        parseDateToken(getDurationParts(role.duration).start),
      );
      const allEnds = sortedRoles.map((role) =>
        parseDateToken(getDurationParts(role.duration).end, true),
      );
      const earliestRole =
        sortedRoles[allStarts.indexOf(Math.min(...allStarts))];
      const latestRole = sortedRoles[allEnds.indexOf(Math.max(...allEnds))];
      const earliestStart = getDurationParts(earliestRole.duration).start;
      const latestEnd = getDurationParts(latestRole.duration).end;
      return {
        ...group,
        roles: sortedRoles,
        overallDuration: `${earliestStart} - ${latestEnd}`,
        sortStart: Math.max(...allStarts),
        sortEnd: Math.max(...allEnds),
      };
    })
    .sort((a, b) => {
      if (b.sortEnd !== a.sortEnd) return b.sortEnd - a.sortEnd;
      return b.sortStart - a.sortStart;
    });

const sortByStartDesc = (items) =>
  [...items].sort((a, b) => {
    const aStart = parseDateToken(getDurationParts(a.duration).start);
    const bStart = parseDateToken(getDurationParts(b.duration).start);
    return bStart - aStart;
  });

const SegmentedControl = ({ options, value, onChange, ariaLabel }) => (
  <div
    className="inline-flex flex-wrap rounded-lg border border-gray-200 p-0.5 text-sm dark:border-gray-700"
    role="tablist"
    aria-label={ariaLabel}
  >
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={value === option.id}
        onClick={() => onChange(option.id)}
        className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
          value === option.id
            ? "bg-green-solid text-white"
            : "text-gray-500 hover:text-green dark:text-gray-400 dark:hover:text-green-dark"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const PrePhdProjectGrid = ({
  projects,
  regionPrefix,
  onOpenProject,
  studioMode,
}) => (
  <div className={PROJECT_GRID_CLASSES}>
    {projects.map((item) => (
      <EditableRegion
        key={item.id}
        regionId={`${regionPrefix}:${item.id}`}
        label={item.title || item.id}
        className="h-full"
      >
        <ProjectCard
          id={item.id}
          img={item.img}
          title={item.title}
          year={item.year}
          description={projectCardBlurb(item.description)}
          onClick={studioMode ? undefined : () => onOpenProject(item.id)}
          ctaLabel="Explore project →"
        />
      </EditableRegion>
    ))}
  </div>
);

const PrePhd = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { studioMode, draft } = useStudio();
  const background =
    studioMode && draft?.bioExpandedParagraphs
      ? draft.bioExpandedParagraphs
      : bioExpandedParagraphs;

  const tabParam = searchParams.get("tab");
  const tab = TABS.some((item) => item.id === tabParam)
    ? tabParam
    : TAB_PROFESSIONAL;

  const kindParam = searchParams.get("kind");
  const projectKind = PROJECT_KINDS.some((item) => item.id === kindParam)
    ? kindParam
    : PROJECT_KINDS[0].id;
  const activeKind =
    PROJECT_KINDS.find((item) => item.id === projectKind) || PROJECT_KINDS[0];
  const activeProjectId =
    tab === TAB_PROJECTS ? searchParams.get("project") || "" : "";

  const updateParams = (mutator) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next, { replace: true });
  };

  const setTab = (nextTab) => {
    updateParams((next) => {
      next.set("tab", nextTab);
      if (nextTab !== TAB_PROJECTS) {
        next.delete("project");
        next.delete("kind");
      } else if (!next.get("kind")) {
        next.set("kind", PROJECT_KINDS[0].id);
      }
    });
  };

  const setProjectKind = (nextKind) => {
    updateParams((next) => {
      next.set("tab", TAB_PROJECTS);
      next.set("kind", nextKind);
      next.delete("project");
    });
  };

  const openProject = (projectId) => {
    updateParams((next) => {
      next.set("tab", TAB_PROJECTS);
      next.set("kind", projectKind);
      next.set("project", projectId);
    });
  };

  const closeProject = () => {
    updateParams((next) => {
      next.delete("project");
    });
  };

  const groupedByOrg = groupByOrganisation(work);
  const groupedFifthRows = groupByOrganisation(fifthrows);
  const exchangeProgrammeSchools = schools.filter((item) => {
    const role = item.role.toLowerCase();
    return (
      role.includes("exchange programme") ||
      role.includes("summer sessions") ||
      item.organisation ===
        "Korea Advanced Institute of Science and Technology" ||
      item.organisation === "University of California, Berkeley" ||
      item.organisation === "Hanyang University"
    );
  });
  const degreeSchools = schools.filter(
    (item) => !exchangeProgrammeSchools.includes(item),
  );
  const sortedDegreeSchools = sortByStartDesc(degreeSchools);
  const sortedExchangeProgrammeSchools = sortByStartDesc(
    exchangeProgrammeSchools,
  );

  const projectsForKind = mergeProjectsWithDraft(
    activeKind.site,
    studioMode && draft?.[activeKind.draftKey]
      ? draft[activeKind.draftKey]
      : null,
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Sidebar page="Background" />
      <main className="flex-1 py-3 lg:py-4 dark:bg-darkBg dark:text-darkText">
        <PageContainer>
          <div className="flex flex-col gap-8">
            <section className="text-left">
              <h1 className="text-2xl xl:text-3xl font-bold text-lightText dark:text-darkText">
                Background
              </h1>
              {background.length > 0 && (
                <div className="mt-4">
                  <EditableRegion
                    regionId="prePhd.background"
                    label="Background"
                  >
                    <div className="space-y-3">
                      {background.map((paragraph, idx) => (
                        <BioParagraph key={idx}>{paragraph}</BioParagraph>
                      ))}
                    </div>
                  </EditableRegion>
                </div>
              )}
              <div className="mt-5">
                <SegmentedControl
                  options={TABS}
                  value={tab}
                  onChange={setTab}
                  ariaLabel="Background sections"
                />
              </div>
            </section>

            {tab === TAB_PROFESSIONAL && (
              <section
                role="tabpanel"
                aria-label="Professional Experience"
                className="text-left"
              >
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Professional Experience
                </h2>
                <div className="mt-1">
                  {groupedByOrg.map((group) => (
                    <div key={group.organisation} className="pt-3 first:pt-2">
                      {group.roles.map((item, index) => (
                        <MentalHealthCard
                          key={`${group.organisation}-${item.role}-${index}`}
                          organisation={group.organisation}
                          role={item.role}
                          duration={item.duration}
                          jobscope={item.jobscope}
                          showOrganisation={index === 0}
                          organisationHeading="h3"
                          alwaysExpanded
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {tab === TAB_EDUCATION && (
              <section
                role="tabpanel"
                aria-label="Education"
                className="text-left"
              >
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Education
                </h2>
                <p className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-3">
                  Academic Programmes
                </p>
                <div className="mt-1">
                  {sortedDegreeSchools.map((item, index) => (
                    <MentalHealthCard
                      key={`degree-${item.organisation}-${index}`}
                      organisation={item.organisation}
                      role={item.role}
                      duration={item.duration}
                      jobscope={(item.description || []).map((description) => ({
                        description,
                      }))}
                      showOrganisation
                      organisationHeading="h3"
                      alwaysExpanded
                    />
                  ))}
                </div>
                <p className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-4">
                  Exchange and Summer Programmes
                </p>
                <div className="mt-1">
                  {sortedExchangeProgrammeSchools.map((item, index) => (
                    <MentalHealthCard
                      key={`exchange-${item.organisation}-${index}`}
                      organisation={item.organisation}
                      role={item.role}
                      duration={item.duration}
                      jobscope={(item.description || []).map((description) => ({
                        description,
                      }))}
                      showOrganisation
                      organisationHeading="h3"
                      alwaysExpanded
                    />
                  ))}
                </div>
              </section>
            )}

            {tab === TAB_CCAS && (
              <section
                role="tabpanel"
                aria-label="Fifth Rows/Co-Curricular Activities"
                className="text-left"
              >
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Fifth Rows / Co-Curricular Activities
                </h2>
                <div className="mt-1">
                  {groupedFifthRows.map((group) => (
                    <div key={group.organisation} className="pt-3 first:pt-2">
                      {group.roles.map((item, index) => (
                        <MentalHealthCard
                          key={`${group.organisation}-${item.role}-${index}`}
                          organisation={group.organisation}
                          role={item.role}
                          duration={item.duration}
                          jobscope={item.jobscope}
                          showOrganisation={index === 0}
                          organisationHeading="h3"
                          alwaysExpanded
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {tab === TAB_PROJECTS && (
              <section
                role="tabpanel"
                aria-label="Projects"
                className="text-left"
              >
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                  <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText sm:mb-1.5">
                    {activeKind.title}
                  </h2>
                  <SegmentedControl
                    options={PROJECT_KINDS}
                    value={projectKind}
                    onChange={setProjectKind}
                    ariaLabel="Project type"
                  />
                </div>
                <p className="mt-3 mb-8 sm:mt-0 sm:mb-4 text-sm text-gray-500 dark:text-gray-400">
                  {activeKind.blurb}
                </p>
                <PrePhdProjectGrid
                  projects={projectsForKind}
                  regionPrefix={activeKind.regionPrefix}
                  onOpenProject={openProject}
                  studioMode={studioMode}
                />
                <ProjectModal
                  projectId={activeProjectId || null}
                  onClose={closeProject}
                />
              </section>
            )}
          </div>
        </PageContainer>
      </main>
      <Footer />
    </div>
  );
};

export default PrePhd;
